/* eslint-disable @typescript-eslint/no-unused-vars */
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnDef } from "@tanstack/react-table";
import Button from "components/base/Button";
import LeadEmailsTable from "components/tables/LeadEmailsTable";
import { LeadEmail } from "data/crm/leadsData";
import axiosInstance from "helpers/api";
import { dateFormatter, getISODate } from "helpers/utils";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import { useEffect, useState, useRef, useCallback } from "react";
import { Form, Modal, Offcanvas, Spinner } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { ungzip } from "pako";
import "react-quill/dist/quill.snow.css";
import DOMPurify from "dompurify";
import toast from "react-hot-toast";
import ReactQuill from "react-quill";
import { Link } from "react-router-dom";

// Modern Email Interface Component
import EmailInterface from "./EmailInterface";

const DealEmails = ({ dealDetails, onRefreshActivities }: { dealDetails: any; onRefreshActivities?: () => Promise<void> }) => {
  const queryParams = new URLSearchParams(window.location.search);
  const type = queryParams.get("type");
  
  const [emails, setEmails] = useState<any[]>([]);
  const [inboxEmails, setInboxEmails] = useState(0);
  const [sentEmails, setSentEmails] = useState(0);
  const [draftEmails, setDraftEmails] = useState(0);
  const [scheduledEmails, setScheduledEmails] = useState(0);
  const [emailNotConnectedMessage, setEmailNotConnectedMessage] = useState("");
  const [status, setStatus] = useState("idle");
  
  // Add refs to prevent infinite loops
  const isFetchingRef = useRef(false);
  const lastFetchedDealId = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Get primary customer from deal details for email functionality
  const primaryCustomer = dealDetails?.customers?.[0];
  const primaryCustomerEmail = primaryCustomer?.email || 
                               primaryCustomer?.customer_email || 
                               primaryCustomer?.lead_email;

  // Also check if deal has related parties with emails
  const hasRelatedPartiesWithEmails = dealDetails?.related_deals?.some((party: any) => 
    party?.lead?.lead_email || party?.email
  );

  // Add some debugging for the deal data
  console.log('🔍 Deal data in DealEmails:', dealDetails);
  console.log('🔍 Primary customer:', primaryCustomer);
  console.log('🔍 Primary customer email:', primaryCustomerEmail);
  console.log('🔍 Deal ID:', dealDetails?.id);
  console.log('🔍 URL type parameter:', type);
  console.log('🔍 useEffect condition check:', {
    hasDealId: !!dealDetails?.id,
    typeCondition: type?.toLowerCase() === "emails" || !type,
    shouldRun: dealDetails?.id && (type?.toLowerCase() === "emails" || !type)
  });

  const fetchEmailsByType = useCallback(async (emailType: string, abortSignal?: AbortSignal) => {
    console.log(`📧 [FRONTEND] Fetching emails for deal ${dealDetails?.id}, primary customer: ${primaryCustomerEmail}, type: ${emailType}`);
    
    try {
      // Use deal-specific endpoint or fallback to customer email
      const url = `/deals/${dealDetails.id}/emails?direction=${emailType}&limit=50&offset=0`;
      console.log(`📧 [FRONTEND] Making API call to: ${url}`);
      
      const response = await axiosInstance.get(url, {
        signal: abortSignal // Add abort signal to prevent stale requests
      });
      
      console.log(`📧 [FRONTEND] Email response status:`, response.status);
      console.log(`📧 [FRONTEND] Email response data:`, response.data);
      console.log(`📧 [FRONTEND] Response structure keys:`, Object.keys(response.data || {}));
      
      // Handle the response structure from our cached endpoint
      const emailData = response.data?.data?.emails || response.data?.emails || response.data || [];
      
      // Debug logging (can be removed in production)
      console.log(`📧 [FRONTEND] Received ${emailData.length} emails for ${emailType}`);
      if (emailData.length > 0) {
        console.log(`📧 [FRONTEND] First email: "${emailData[0]?.subject}" - Body: ${emailData[0]?.body ? 'HAS CONTENT' : 'NO CONTENT'}`);
      }
      
      // Enhance emails with UI state (since backend doesn't track these)
      const enhancedEmails = emailData.map((email: any) => ({
        ...email,
        is_read: email.direction === 'sent' ? true : false, // Sent emails are always "read"
        is_starred: false, // Default value
        is_ai_generated: false // Default value, will be true for actual AI emails
      }));
      
      return enhancedEmails;
    } catch (error: any) {
      // Don't set status here, let the caller handle it
      console.error('❌ [DEAL EMAILS] Error fetching emails:', error);
      console.error('❌ [DEAL EMAILS] Error response:', error.response);
      console.error('❌ [DEAL EMAILS] Error status:', error.response?.status);
      console.error('❌ [DEAL EMAILS] Error details:', error.response?.data);
      console.error('❌ [DEAL EMAILS] Error message:', error.message);
      
      // If request was aborted, don't set error state
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        console.log('📧 [FRONTEND] Request aborted for emailType:', emailType);
        return [];
      }
      
      // If it's a 404 or email connection issue, set appropriate message
      if (error.response?.status === 404) {
        setEmailNotConnectedMessage("No emails found for this deal.");
      } else if (error.response?.status === 401) {
        setEmailNotConnectedMessage("Authentication error. Please reconnect your email.");
      } else if (error.response?.status === 403) {
        setEmailNotConnectedMessage("Access denied. Please check your email permissions.");
      } else if (error.response?.status === 500) {
        const serverError = error.response?.data?.error || error.response?.data?.message || 'Server error';
        setEmailNotConnectedMessage(`Server error: ${serverError}`);
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        setEmailNotConnectedMessage("Network error. Please check your connection.");
      } else {
        const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
        setEmailNotConnectedMessage(`Error loading emails: ${errorMsg}`);
      }
      
      throw error; // Re-throw to let caller handle
    }
  }, [dealDetails?.id, primaryCustomerEmail]);

  const handleRefreshEmails = useCallback(async (emailType: string) => {
    console.log(`🔄 Refreshing ${emailType} emails`);
    
    // Don't refetch if we're in the middle of sending an email
    if (status === "sending") {
      console.log(`🔄 Skipping refresh because we're sending an email`);
      return emails;
    }
    
    try {
      setStatus("loading");
      
      // Fetch both received and sent emails to ensure complete refresh
      const [receivedEmails, sentEmails] = await Promise.all([
        fetchEmailsByType("received"),
        fetchEmailsByType("sent")
      ]);
      
      // Update both the combined emails array and individual counts
      const allEmails = [...receivedEmails, ...sentEmails];
      setEmails(allEmails);
      setInboxEmails(receivedEmails.length);
      setSentEmails(sentEmails.length);
      
      console.log(`✅ [REFRESH] Updated email counts - Inbox: ${receivedEmails.length}, Sent: ${sentEmails.length}`);
      
      setStatus("idle");
      return allEmails;
    } catch (error) {
      console.error(`❌ [REFRESH] Error refreshing emails:`, error);
      setStatus("idle");
      return emails;
    }
  }, [fetchEmailsByType, status, emails]);

  useEffect(() => {
    console.log('🔄 useEffect triggered with:', { dealId: dealDetails?.id, type, condition: dealDetails?.id && (type?.toLowerCase() === "emails" || !type) });
    
    // Load emails when on the Emails tab (type="emails") OR when no type is specified (default to emails)
    if (dealDetails?.id && (type?.toLowerCase() === "emails" || !type)) {
      console.log(`🚀 Initializing email data for deal:`, dealDetails.id, `(type: ${type || 'default'})`);
      
      (async () => {
        try {
          // Phase 1: Use cached database emails for fast loading
          console.log(`⚡ [CACHE] Loading emails from database cache`);
          setStatus("loading");
          
          // Fetch emails and counts from cached database
          const [receivedEmails, sentEmails] = await Promise.all([
            fetchEmailsByType("received"),
            fetchEmailsByType("sent")  // Actually FETCH sent emails, don't just count
          ]);
          
          // Combine both received and sent emails into the main emails array
          const allEmails = [...receivedEmails, ...sentEmails];
          setEmails(allEmails);
          
          setInboxEmails(receivedEmails.length);
          setSentEmails(sentEmails.length);
          
          // TODO: Add actual draft and scheduled counts when backend supports them
          setDraftEmails(0);
          setScheduledEmails(0);
          
          console.log(`📊 [CACHE] Email counts - Inbox: ${receivedEmails.length}, Sent: ${sentEmails.length}`);
          setStatus("idle");
        } catch (error) {
          console.error('❌ [CACHE] Error in email initialization:', error);
          setStatus("idle");
        }
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, dealDetails?.id]);

  // Check if deal has a primary customer with an email address - only show warning if no customer AND no way to get emails
  if (!primaryCustomerEmail || primaryCustomerEmail.trim() === '') {
    // If there's a deal ID, we can still fetch emails from related parties, so don't block the component
    if (!dealDetails?.id) {
      return (
        <div className="alert alert-phoenix-warning text-center fs-7">
          <div className="mb-2">
            <strong>No Deal Selected</strong>
          </div>
          <p className="mb-2">Please select a deal to view emails.</p>
        </div>
      );
    }
    // If we have a deal ID but no customer email, still render the component
    // The backend will fetch emails from related parties if they exist
  }

  const handleSendEmail = async (emailData: any) => {
    console.log(`📤 Sending email for deal:`, emailData);
    
    // Check if we should skip refresh
    const skipRefresh = emailData.skipRefresh;
    
    try {
      // Set status to prevent skeleton loading during email sending
      if (!skipRefresh) {
        setStatus("sending"); // Special status to indicate email sending in progress
      }
      
      const payload: any = {
        to: emailData.to,
        subject: emailData.subject,
        message: emailData.message,
        deal_id: dealDetails?.id, // Link email to deal
      };

      if (emailData.cc) {
        payload.cc = emailData.cc;
      }

      if (emailData.bcc) {
        payload.bcc = emailData.bcc;
      }

      // If there are attachments, handle them with FormData
      if (emailData.attachments && emailData.attachments.length > 0) {
        const formData = new FormData();
        
        // Add all the text fields
        Object.keys(payload).forEach(key => {
          formData.append(key, payload[key]);
        });
        
        // Add attachments
        Array.from(emailData.attachments).forEach((file: any, i: number) => {
          formData.append(`attachments`, file);
        });
        
        // Send as multipart/form-data
        const response = await axiosInstance.post('emails', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        console.log(`✅ Email with attachments sent successfully:`, response.data);
        
        // After successful send, add it to the emails state
        const newEmail = {
          id: response.data.id || `temp-${Date.now()}`,
          subject: emailData.subject,
          from: 'You',  // Assuming user is sender
          to: emailData.to,
          body: emailData.message,
          email_creation_date: new Date().toISOString(),
          direction: 'sent',
          is_read: true,
          is_starred: false
        };
        
        setEmails(prev => [newEmail, ...prev]);
        setSentEmails(prev => prev + 1);
        
        // Refresh activities timeline after email is sent and database is updated
        if (onRefreshActivities) {
          // Add a small delay to ensure the database is consistent
          setTimeout(async () => {
            try {
              await onRefreshActivities();
              console.log('✅ Activities timeline refreshed after email send');
            } catch (error) {
              console.warn('⚠️ Failed to refresh activities timeline:', error);
            }
          }, 1000); // 1 second delay to ensure database consistency
        }
        
        return Promise.resolve({data: response.data, emails: [...emails, newEmail]});
      } else {
        // Send without attachments as regular JSON
        const response = await axiosInstance.post('emails', payload);
        console.log(`✅ Email sent successfully:`, response.data);
        
        // After successful send, add it to the emails state
        const newEmail = {
          id: response.data.id || `temp-${Date.now()}`,
          subject: emailData.subject,
          from: 'You',  // Assuming user is sender
          to: emailData.to,
          body: emailData.message,
          email_creation_date: new Date().toISOString(),
          direction: 'sent',
          is_read: true,
          is_starred: false
        };
        
        setEmails(prev => [newEmail, ...prev]);
        setSentEmails(prev => prev + 1);
        
        // Refresh activities timeline after email is sent and database is updated
        if (onRefreshActivities) {
          // Add a small delay to ensure the database is consistent
          setTimeout(async () => {
            try {
              await onRefreshActivities();
              console.log('✅ Activities timeline refreshed after email send');
            } catch (error) {
              console.warn('⚠️ Failed to refresh activities timeline:', error);
            }
          }, 1000); // 1 second delay to ensure database consistency
        }
        
        return Promise.resolve({data: response.data, emails: [...emails, newEmail]});
      }
    } catch (error: any) {
      console.error('❌ Error sending email:', error.response?.data || error);
      return Promise.reject(error); 
    } finally {
      // Reset status to succeeded to avoid skeleton loaders
      if (!skipRefresh) {
        setStatus("succeeded");
      }
    }
  };

  const handleCreateDraft = async (draftData: any) => {
    console.log(`📝 Creating draft for deal:`, draftData);
    
    try {
      // For now, simulate draft creation by adding to local state
      // TODO: Implement actual backend API when available
      const newDraft = {
        id: `draft-${Date.now()}`,
        subject: draftData.subject,
        body: draftData.message,  // Use 'body' to match backend format
        from: 'AI Assistant',
        to: primaryCustomerEmail,
        email_creation_date: new Date().toISOString(),
        direction: 'sent',
        is_ai_generated: true,
        draft_confidence: draftData.draft_confidence || 0.95,
        is_read: true,
        is_starred: false
      };
      
      setEmails(prev => [newDraft, ...prev]);
      setDraftEmails(prev => prev + 1);
      
      toast.success('AI Draft created successfully!');
      return Promise.resolve({ data: newDraft });
    } catch (error: any) {
      console.error('❌ Error creating draft:', error);
      return Promise.reject(error);
    }
  };

  const handleReplyEmail = async (emailId: string, replyData: any) => {
    console.log(`↩️ Replying to email ${emailId} for deal:`, replyData);
    
    // Check if we should skip refresh
    const skipRefresh = replyData.skipRefresh;
    
    try {
      // Set status to prevent skeleton loading during reply sending
      if (!skipRefresh) {
        setStatus("sending"); // Special status to indicate email sending in progress
      }
      
      const response = await axiosInstance.post(`emails/reply`, {
        to: primaryCustomerEmail,
        message: replyData.message,
        subject: replyData.subject,
        threadId: replyData.threadId,
        messageId: emailId,
        deal_id: dealDetails?.id, // Link reply to deal
      });
      
      // Add the reply to the local emails state to avoid refetching
      const newReply = {
        id: response.data.id || `reply-${Date.now()}`,
        subject: replyData.subject,
        from: 'You',
        to: replyData.to || primaryCustomerEmail,
        body: replyData.message,
        email_creation_date: new Date().toISOString(),
        direction: 'sent',
        thread_id: replyData.threadId,
        is_read: true,
        is_starred: false
      };
      
      // Update state with new reply
      setEmails(prev => [newReply, ...prev]);
      setSentEmails(prev => prev + 1);
      
      // Refresh activities timeline after reply is sent
      if (onRefreshActivities) {
        setTimeout(async () => {
          try {
            await onRefreshActivities();
            console.log('✅ Activities timeline refreshed after reply send');
          } catch (error) {
            console.warn('⚠️ Failed to refresh activities timeline:', error);
          }
        }, 1000);
      }
      
      return Promise.resolve({data: response.data, emails: [...emails, newReply]});
    } catch (error: any) {
      console.error('❌ Error sending reply:', error.response?.data || error);
      return Promise.reject(error);
    } finally {
      // Reset status to succeeded to avoid skeleton loaders
      if (!skipRefresh) {
        setStatus("succeeded");
      }
    }
  };

  const handleDeleteEmail = async (emailId: string) => {
    console.log(`🗑️ Deleting email ${emailId}`);
    
    try {
      // For now, just remove from local state since backend deletion may not be implemented
      setEmails(prev => prev.filter((email: any) => email.id !== emailId));
      toast.success('Email deleted successfully!');
      return Promise.resolve();
    } catch (error: any) {
      console.error('❌ Error deleting email:', error);
      return Promise.reject(error); 
    }
  };

  const handleUpdateEmail = async (emailId: string, updates: any) => {
    console.log(`✏️ Updating email ${emailId}:`, updates);
    
    try {
      // Since the backend doesn't have PATCH /emails/{id} endpoint,
      // we'll update the local state only for UI features like starring and read status
      setEmails(prev => prev.map((email: any) => 
        email.id === emailId ? { ...email, ...updates } : email
      ));
      
      // Show appropriate feedback
      if (updates.is_starred !== undefined) {
        toast.success(updates.is_starred ? 'Email starred!' : 'Email unstarred!');
      }
      if (updates.is_read !== undefined) {
        console.log(`📖 Email marked as ${updates.is_read ? 'read' : 'unread'}`);
      }
      
      return Promise.resolve({ data: { success: true } });
    } catch (error: any) {
      console.error('❌ Error updating email:', error);
      return Promise.reject(error);
    }
  };

  // Manual force refresh function for debugging
  const handleForceRefresh = async () => {
    console.log('🔄 [FORCE REFRESH] Manually refreshing deal emails...');
    setEmailNotConnectedMessage("");
    setStatus("loading");
    
    try {
      // Step 1: Clear backend cache first
      console.log('🗑️ [FORCE REFRESH] Clearing backend cache...');
      try {
        await axiosInstance.delete(`/deals/${dealDetails.id}/emails/cache`);
        console.log('✅ [FORCE REFRESH] Backend cache cleared');
      } catch (cacheError: any) {
        console.warn('⚠️ [FORCE REFRESH] Cache clear failed (continuing anyway):', cacheError.message);
      }

      // Step 2: Force clear frontend state
      setEmails([]);
      setInboxEmails(0);
      setSentEmails(0);
      
      // Step 3: Wait a moment for cache clear to propagate
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Step 4: Fetch fresh data
      console.log('📧 [FORCE REFRESH] Fetching fresh emails...');
      const [receivedEmails, sentEmails] = await Promise.all([
        fetchEmailsByType("received"),
        fetchEmailsByType("sent")
      ]);
      
      const allEmails = [...receivedEmails, ...sentEmails];
      setEmails(allEmails);
      setInboxEmails(receivedEmails.length);
      setSentEmails(sentEmails.length);
      
      console.log(`✅ [FORCE REFRESH] Completed - Inbox: ${receivedEmails.length}, Sent: ${sentEmails.length}`);
      
      if (allEmails.length === 0) {
        console.log('⚠️ [FORCE REFRESH] No emails returned - check browser network tab for API errors');
      }
      
      setStatus("idle");
    } catch (error) {
      console.error('❌ [FORCE REFRESH] Error:', error);
      setStatus("idle");
    }
  };

  // Create a lead-like object for the EmailInterface component
  const leadLikeObject = {
    id: dealDetails?.id,
    lead_email: primaryCustomerEmail,
    lead_first_name: primaryCustomer?.first_name || primaryCustomer?.customer_first_name || 'Customer',
    lead_last_name: primaryCustomer?.last_name || primaryCustomer?.customer_last_name || '',
    deal_name: dealDetails?.deal_name
  };

  // Add some debugging for the deal data
  console.log('🔍 Deal data:', dealDetails);
  console.log('🔍 Lead-like object:', leadLikeObject);
  console.log('🔍 Current emails:', emails);
  console.log('🔍 Email counts:', { inbox: inboxEmails, sent: sentEmails });
  console.log('🔍 Status:', status);

  if (emailNotConnectedMessage) {
    return (
      <div className="alert alert-phoenix-danger text-center fs-7">
        {emailNotConnectedMessage}
        {status === 'loading' && (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        )}
        <div className="d-flex justify-content-center align-items-center mt-2">
          <Button
            as={Link}
            to="/settings/email-settings"
            variant="phoenix-primary"
            size="sm"
          >
            Connect Email
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-100" style={{maxWidth: "100%"}}>
      {/* Debug Refresh Button */}
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <div>
          <small className="text-muted">
            Debug: {emails.length} emails loaded | Status: {status} | Inbox: {inboxEmails} | Sent: {sentEmails} | Customer: {primaryCustomerEmail}
          </small>
        </div>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={handleForceRefresh}
          disabled={status === "loading"}
        >
          🔄 Force Refresh
        </Button>
      </div>
      
      <EmailInterface
        lead={leadLikeObject}
        emails={emails}
        status={status}
        onSendEmail={handleSendEmail}
        onCreateDraft={handleCreateDraft}
        onReplyEmail={handleReplyEmail}
        onDeleteEmail={handleDeleteEmail}
        onUpdateEmail={handleUpdateEmail}
        onRefreshEmails={handleRefreshEmails}
        inboxCount={inboxEmails}
        sentCount={sentEmails}
        draftCount={draftEmails}
        scheduledCount={scheduledEmails}
      />
    </div>
  );
};

export default DealEmails;